import React, { useState, useContext, useEffect } from 'react'
import { FormikContext } from 'formik'
import { FiX, FiZoomIn, FiPlus, FiEye } from 'react-icons/fi'
import Modal from '../Modal'
import Spinner from '../Spinner'
import { ImageZoomContainer } from './styles'
import { Button } from '../Form'

function useSafeFormikContext() {
  	return useContext(FormikContext)
}

export default function ({ files, name, uploadFieldName }) {
	const formik = useSafeFormikContext()

	const [showWithZoom, setShowWithZoom] = useState(null)
	const [fileURLs, setFileURLs] = useState([])
	const [loading, setLoading] = useState(true)

	async function loadFileURLs() {
		const fileURLs = []

		for(const arq of files) {
			if(arq.link) {
				fileURLs.push({
					...arq,
					url: arq.link,
					extension: arq.link.split('.').at(-1),
					key: arq.id
				})
			} else { // tipo File
				fileURLs.push({
					...arq,
					url: URL.createObjectURL(arq),
					extension: arq.name.split('.').at(-1),
					key: arq.lastModified
				})
			}
		}

		setFileURLs(fileURLs)
		setLoading(false)
	}

	useEffect(() => {
		loadFileURLs()
	}, [files])

	function handleDeleteFile(index) {
		if(formik) {
			const arquivoAtuais = formik.values[name]
	
			formik.setFieldValue(name, arquivoAtuais.filter((_, idx) => idx !== index))
		}
	}

	function handleOpenNewTab(url) {
		window.open(url, '_blank')
	}

	if(!fileURLs) {
		return <Spinner />
	}

	return (
		<div className="my-3 flex flex-col">
			{loading ? (
				<Spinner />
			) : !!fileURLs.length ? (
				<>
					<ul className="grid grid-cols-3 gap-4 md:gap-0 md:flex md:flex-wrap my-4 group w-fit ml-4">
						{fileURLs.map((arq, idx) => {
							const { url, extension, key, name } = arq
							const isFirst = idx === 0

							return (
								<li
									key={key}
									className={`
										relative group border p-2 rounded-md border-slate-300 bg-white hover:scale-110 select-none md:-rotate-[8deg]
										w-24 md:w-28 whitespace-nowrap overflow-hidden text-ellipsis transition-all md:group-hover:rotate-0
										${isFirst ? '' : 'md:-ml-24 md:group-hover:ml-0'}	
									`}
								>
									{extension === 'pdf' ? (
										<iframe src={url} width={112} height={158} />
									) : (
										<img src={url} alt="" className="w-24 h-36 object-cover" />
									)}

									<span>{name}</span>

									{formik && (
										<div className="absolute hidden group-hover:flex right-1 top-1 rounded-full p-1 bg-white shadow-md cursor-pointer w-8 h-8 justify-center items-center">
											<FiX 
												size={20} 
												color="#424242"
												className="" 
												onClick={() => { 
													handleDeleteFile(idx) 
												}} 
											/>
										</div>
									)}

									<div 
										className="absolute hidden group-hover:flex left-1/2 top-1/2 rounded-full p-1 bg-white shadow-md cursor-pointer w-10 h-10 justify-center items-center"
										style={{ transform: 'translate(-50%, -70%)' }}
									>
										<FiZoomIn 
											size={24} 
											color="#424242"
											className="cursor-pointer" 
											onClick={() => {
												setShowWithZoom({ 
													url, 
													extension,
													name
												})
											}}
										/>
									</div>
								</li>
							)
						})}

						{formik && uploadFieldName && (
							<li
								className={`
									relative border p-2 rounded-md border-slate-300 bg-white hover:scale-110 select-none 
									w-24 md:w-28 transition-all md:-ml-12 md:group-hover:ml-0
								`}
							>
								<label htmlFor={uploadFieldName} className="flex flex-col items-center justify-center gap-3 h-full">
									<FiPlus size={44} className="text-slate-700/50 border rounded-full p-3 cursor-pointer group-hover:text-slate-700" />
									<span style={{ fontSize: 12 }} className="text-slate-700/50 group-hover:text-slate-700">Incluir páginas</span>
								</label>
							</li>
						)}
					</ul>

					<Modal
						isOpen={!!showWithZoom?.url}
						handleClose={() => {
							setShowWithZoom(null)
						}}
						title={showWithZoom?.name}
					>
						{showWithZoom?.url && (
							<div className="flex justify-center flex-col gap-4">
								<Button className="w-fit self-end" onClick={() => { handleOpenNewTab(showWithZoom.url) }}>
									Ampliar imagem
									<FiEye size={16} />
								</Button>
								
								{showWithZoom.extension === 'pdf' ? (
									<iframe src={showWithZoom.url} className="border-0 w-full h-[64vh]" />
								) : (
									<>
										<div className="md:hidden flex flex-col gap-8">
											<img 
												src={showWithZoom.url} 
												alt="" 
												className="h-[64vh] object-contain" 
												style={{
													boxShadow: '0 4px 5px 0 rgba(0,0,0,0.14),0 1px 10px 0 rgba(0,0,0,0.12),0 2px 4px -1px rgba(0,0,0,0.3)'
												}} 
											/>
										</div>

										<div className="hidden md:block">
											<ImageZoomContainer
												src={showWithZoom.url} 
												alt=""
											/>
										</div>
									</>
								)}
							</div>
						)}
					</Modal>
				</>
			) : (
				<div className="relative w-fit md:self-center">
					<div className="flex mx-2 mt-3 relative">
						<div className="group p-2 rounded-md h-36 w-28 bg-slate-300/40"></div>
						<div className="group p-2 rounded-md h-36 w-28 bg-slate-300/40 -ml-24 mt-2"></div>
						<div className="group p-2 rounded-md h-36 w-28 bg-slate-300/40 -ml-24 mt-4"></div>
						<div className="group p-2 rounded-md h-36 w-28 bg-slate-300/40 -ml-24 mt-6"></div>
					</div>

					<div className="mx-2 my-6 absolute left-16 top-12 w-96 text-slate-600">Nenhum arquivo anexado</div>

					<label htmlFor={uploadFieldName} className="absolute flex flex-col items-center justify-center gap-3 left-12 ml-1 top-24 mt-3 w-48">
						<div 
							className="bg-emerald-600 text-white rounded-md h-[42px] px-[14px] cursor-pointer flex justify-center items-center hover:opacity-80 transition-opacity"
							style={{
								boxShadow: 'rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px, rgba(0, 0, 0, 0.3) 0px 2px 4px -1px'
							}}
						>
							Selecionar arquivos
						</div>
					</label>
				</div>
			)}
		</div>
	)
}
